import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import { CheckboxType, CheckboxState } from '@nebula/checkbox';
import type { CheckboxProps } from '@nebula/checkbox';
import Select from '@nebula/select';
import TextInput, { TextInputState } from '@nebula/text-input';
import GlobalStyles, { BodyLarge400 } from '@nebula/global-styles';
import { InformationAlert } from '@nebula/alert';
import Analytics, { InteractionInputEvent } from '@ecs-uk/web-common-analytics';
import { setAutomationElement } from '@experian-uk/corvetteuk-common-ui';
import { ErrorIcon } from '@nebula/icons/ui';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const Checkbox = require('@nebula/checkbox/dist/cjs/Checkbox').default as React.FC<CheckboxProps>;
import {
  Container,
  Header,
  StyledTitle,
  SubInputWrapper,
  CharacterCount,
  ButtonGroup,
  ContinueButton,
  BackButton,
  CheckboxItemWrapper,
  CheckboxGroupWrapper,
  AlertWrapper,
  ValidationError,
} from './index.styles';
import usePageView from '../../../../hooks/usePageView';

const REASON_VALUES = {
  ACHIEVED_GOAL: 'achieved_goal',
  DOESNT_PROVIDE: 'doesnt_provide_what_i_need',
  DONT_UNDERSTAND: 'dont_understand',
  WANT_TO_PAUSE: 'want_to_pause',
  USE_ANOTHER_SERVICE: 'use_another_service',
  TOO_EXPENSIVE: 'too_expensive',
  OTHER: 'other',
} as const;

type ReasonValue = (typeof REASON_VALUES)[keyof typeof REASON_VALUES];

const REASONS: Array<{ value: ReasonValue; label: string; paidOnly?: boolean }> = [
  { value: REASON_VALUES.TOO_EXPENSIVE, label: "It's too expensive", paidOnly: true },
  { value: REASON_VALUES.ACHIEVED_GOAL, label: "I've achieved my goal" },
  { value: REASON_VALUES.DOESNT_PROVIDE, label: "It doesn't provide what I need" },
  { value: REASON_VALUES.DONT_UNDERSTAND, label: "I don't understand how it works" },
  { value: REASON_VALUES.WANT_TO_PAUSE, label: 'I want to pause it now and may return later' },
  { value: REASON_VALUES.USE_ANOTHER_SERVICE, label: 'I use another service instead' },
  { value: REASON_VALUES.OTHER, label: 'Other' },
];

const COMPETITOR_OPTIONS = [
  { label: 'Clear Score', value: 'clear_score' },
  { label: 'Equifax', value: 'equifax' },
  { label: 'Credit Karma', value: 'credit_karma' },
  { label: 'Totally Money', value: 'totally_money' },
  { label: 'My bank', value: 'my_bank' },
  { label: 'Other', value: 'other' },
];

const MAX_CHARS = 50;

type PiiValidationKind = 'email' | 'phone' | 'customer_number';

type TextValidationResult = { kind: PiiValidationKind; message: string };

const VALIDATION_VIEW_COMPONENT: Record<PiiValidationKind, string> = {
  email: 'deactivation_reason_validation_email',
  phone: 'deactivation_reason_validation_phone',
  customer_number: 'deactivation_reason_validation_customer_number',
};

const VALIDATION_KINDS: PiiValidationKind[] = ['email', 'phone', 'customer_number'];

const validateTextInput = (value: string): TextValidationResult | null => {
  if (!value) return null;
  if (/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(value)) {
    return { kind: 'email', message: 'Please remove your email' };
  }
  if (/(\+?44[\s-]?|0)\d[\d\s-]{7,}\d/.test(value.replace(/\s/g, ''))) {
    return { kind: 'phone', message: 'Please remove phone number' };
  }
  if (/\b\d{6,}\b/.test(value)) {
    return { kind: 'customer_number', message: 'Please remove your customer ID' };
  }
  return null;
};

export interface SelectedReasonItem {
  reason: string;
  additionalComment: string;
}

export type DeactivationReasonData = SelectedReasonItem[];

interface ReasonCaptureStageProps {
  onContinue: (data: DeactivationReasonData) => void;
  onBack: () => void;
  isPaidUser?: boolean;
  isDeferred?: boolean;
  isCoolingOffEligible?: boolean;
  productDisplayName?: string;
  formattedNextBillingDate?: string;
  isWebview?: boolean;
}

const needsTextInput = (reason: ReasonValue) =>
  reason === REASON_VALUES.DOESNT_PROVIDE || reason === REASON_VALUES.OTHER;

const TEXT_DETAIL_REASONS: ReasonValue[] = [REASON_VALUES.DOESNT_PROVIDE, REASON_VALUES.OTHER];

const needsDropdown = (reason: ReasonValue) => reason === REASON_VALUES.USE_ANOTHER_SERVICE;

const ReasonCaptureStage: FC<ReasonCaptureStageProps> = ({
  onContinue,
  onBack,
  isPaidUser = false,
  isDeferred = false,
  productDisplayName = 'Identity Plus',
  formattedNextBillingDate = '',
  isWebview = false,
}) => {
  const PAGE_NAME = 'ProdMoveDeactivationReasonPage';

  usePageView(PAGE_NAME);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedReasons, setSelectedReasons] = useState<SelectedReasonItem[]>([]);
  const validationViewPublishedRef = useRef<Set<string>>(new Set());

  const isReasonSelected = (reasonValue: ReasonValue) => selectedReasons.some(r => r.reason === reasonValue);

  const getAdditionalComment = (reasonValue: ReasonValue) =>
    selectedReasons.find(r => r.reason === reasonValue)?.additionalComment ?? '';

  const getTextError = (reasonValue: ReasonValue) => {
    if (!needsTextInput(reasonValue)) return null;
    return validateTextInput(getAdditionalComment(reasonValue))?.message ?? null;
  };

  const hasValidationError = selectedReasons.some(
    r => needsTextInput(r.reason as ReasonValue) && !!validateTextInput(r.additionalComment)
  );

  const handleReasonToggle = useCallback((reasonValue: ReasonValue) => {
    setSelectedReasons(prev => {
      const exists = prev.some(r => r.reason === reasonValue);
      if (exists) {
        return prev.filter(r => r.reason !== reasonValue);
      }
      return [...prev, { reason: reasonValue, additionalComment: '' }];
    });
  }, []);

  const handleAdditionalCommentChange = useCallback((reasonValue: ReasonValue, value: string) => {
    setSelectedReasons(prev =>
      prev.map(r =>
        r.reason === reasonValue
          ? { ...r, additionalComment: needsTextInput(reasonValue) ? value.slice(0, MAX_CHARS) : value }
          : r
      )
    );
  }, []);

  useEffect(() => {
    const selectedDetailReasons = new Set(
      selectedReasons.filter(r => needsTextInput(r.reason as ReasonValue)).map(r => r.reason)
    );

    TEXT_DETAIL_REASONS.forEach(rv => {
      if (!selectedDetailReasons.has(rv)) {
        VALIDATION_KINDS.forEach(k => validationViewPublishedRef.current.delete(`${rv}-${k}`));
      }
    });

    selectedReasons.forEach(({ reason, additionalComment }) => {
      const rv = reason as ReasonValue;
      if (!needsTextInput(rv)) return;

      const result = validateTextInput(additionalComment);
      if (!result) {
        VALIDATION_KINDS.forEach(k => validationViewPublishedRef.current.delete(`${reason}-${k}`));
        return;
      }

      const dedupeKey = `${reason}-${result.kind}`;
      if (validationViewPublishedRef.current.has(dedupeKey)) return;

      validationViewPublishedRef.current.add(dedupeKey);
      Analytics.publish(
        InteractionInputEvent.fromObject({
          component: VALIDATION_VIEW_COMPONENT[result.kind],
          action: 'view',
          status: 'success',
        })
      );
    });
  }, [selectedReasons]);

  const handleContinue = useCallback(() => {
    Analytics.publish(
      InteractionInputEvent.fromObject({ component: 'continue_deactivate', action: 'click', status: 'success' })
    );
    onContinue(selectedReasons);
  }, [onContinue, selectedReasons]);

  const handleFormSubmit = useCallback(
    (event: React.SyntheticEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleContinue();
    },
    [handleContinue]
  );

  const handleBackToPersonalDetails = useCallback(() => {
    Analytics.publish(
      InteractionInputEvent.fromObject({ component: 'back_to_personal_details', action: 'click', status: 'success' })
    );
    onBack();
  }, [onBack]);

  return (
    <Container $isWebview={isWebview}>
      <GlobalStyles />
      {isDeferred && formattedNextBillingDate && (
        <AlertWrapper>
          <InformationAlert
            message={`You'll still have access to ${productDisplayName} until ${formattedNextBillingDate}.`}
            {...setAutomationElement('deactivation-deferred-alert')}
          />
        </AlertWrapper>
      )}
      <Header>
        <StyledTitle as="h1">We&apos;re sorry to see you go</StyledTitle>
        <BodyLarge400>
          Answering this really helps us improve our services. This won&apos;t affect your deactivation.
        </BodyLarge400>
      </Header>
      <form id="deactivation_reason_form" onSubmit={handleFormSubmit}>
        <CheckboxGroupWrapper aria-label="Deactivation reason">
          {REASONS.filter(reason => !reason.paidOnly || isPaidUser).map((reason, index) => {
            const isSelected = isReasonSelected(reason.value);
            const additionalComment = getAdditionalComment(reason.value);
            const textError = isSelected ? getTextError(reason.value) : null;

            return (
              <CheckboxItemWrapper key={reason.value}>
                <Checkbox
                  data-sp-reveal
                  id={`deactivation_reason_${reason.value}`}
                  label={reason.label}
                  value={reason.value}
                  checked={isSelected}
                  index={index}
                  checkboxType={CheckboxType.Outlined}
                  checkboxState={CheckboxState.Standard}
                  onChange={() => handleReasonToggle(reason.value)}
                  width="100%"
                />

                {isSelected && needsTextInput(reason.value) && (
                  <SubInputWrapper $hasError={!!textError}>
                    <TextInput
                      uniqueId={`reason-detail-${reason.value}`}
                      helperTop="Please don't leave any personal or contact information here."
                      value={additionalComment}
                      onChange={e => handleAdditionalCommentChange(reason.value, e.target.value)}
                      state={TextInputState.Standard}
                      {...setAutomationElement('deactivation-reason-detail')}
                    />
                    {textError && (
                      <ValidationError>
                        <ErrorIcon iconColor="#FA1320" />
                        {textError}
                      </ValidationError>
                    )}
                    <CharacterCount $isError={!!textError}>
                      {additionalComment.length}/{MAX_CHARS}
                    </CharacterCount>
                  </SubInputWrapper>
                )}

                {isSelected && needsDropdown(reason.value) && (
                  <SubInputWrapper $isSelect>
                    <Select
                      data-sp-reveal
                      config={COMPETITOR_OPTIONS}
                      value={additionalComment}
                      onChange={e => handleAdditionalCommentChange(reason.value, e.target.value)}
                      width="100%"
                      placeholder="Select"
                      {...setAutomationElement('deactivation-reason-service')}
                    />
                  </SubInputWrapper>
                )}
              </CheckboxItemWrapper>
            );
          })}
        </CheckboxGroupWrapper>
      </form>

      <ButtonGroup $isWebview={isWebview}>
        <ContinueButton
          isButton
          buttonKind="primaryMagenta"
          type="submit"
          form="deactivation_reason_form"
          label="Continue"
          disabled={hasValidationError}
          {...setAutomationElement('deactivation-reason-continue')}
        />
        <BackButton
          isButton
          buttonKind="secondaryMagenta"
          htmlType="button"
          label="Back to personal details"
          onClick={handleBackToPersonalDetails}
          {...setAutomationElement('deactivation-reason-back')}
        />
      </ButtonGroup>
    </Container>
  );
};

export default ReasonCaptureStage;
